import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  ButtonComponent,
  IPaginationEvent,
  ModalService,
  PaginationComponent,
  SvgIconComponent,
  TableComponent,
} from '@ui-lib/components';
import { TranslateModule } from '@ngx-translate/core';
import { getRolesTableColumnsConfig } from './roles-table-columns.config';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RolesService } from './services/roles.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { debounceTime, filter, take } from 'rxjs';
import { RoleModalComponent } from './components/role-modal/role-modal.component';
import { IRoleModalData, IRoleModalFormChecker, RolesAction } from './roles.model';
import { ConfirmationDialogComponent } from '../../shared/components/confirm-dialog/confirmation-dialog.component';
import { IConfirmationDialogData } from '../../shared/components/confirm-dialog/confirmation-dialog-data.interface';
import { LoadingState } from '../../shared/types/loading.type';
import { PermissionService } from '../../core/services/user/permission.service';
import { UserPermissionEnum } from '../../core/models/user-permissions.enum';
import { PageToolbarComponent } from '../../shared/components/page-toolbar/page-toolbar.component';
import { PageToolbarAction } from '../../shared/components/page-toolbar/page-toolbar.model';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../shared/const/pagination.constants';

@Component({
  standalone: true,
  selector: 'mon-roles',
  templateUrl: './roles.component.html',
  styleUrls: ['./roles.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RolesService],
  imports: [
    ButtonComponent,
    SvgIconComponent,
    TranslateModule,
    TableComponent,
    ReactiveFormsModule,
    PaginationComponent,
    AsyncPipe,
    PageToolbarComponent,
  ],
})
export class RolesComponent implements OnInit {
  private readonly rolesService = inject(RolesService);
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly permissionService = inject(PermissionService);

  public readonly rolesLoadingState: Signal<LoadingState> = this.rolesService.rolesLoadingState;
  public readonly totalCount: WritableSignal<number> = this.rolesService.totalRolesCount;
  public readonly totalCountWithoutFilter: WritableSignal<number> =
    this.rolesService.totalRolesCountWithoutFilter;

  protected readonly LoadingState = LoadingState;
  protected readonly RolesAction = RolesAction;
  protected readonly pagination: WritableSignal<IPaginationEvent> = signal({
    pageIndex: 1,
    pageSize: DEFAULT_TABLE_PAGE_SIZE,
  });
  protected readonly roles$ = this.rolesService.roles$;
  protected readonly canManageRoles = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.RoleManage),
    { initialValue: false },
  );

  protected columns = getRolesTableColumnsConfig();
  protected searchControl = new FormControl<string>('', { nonNullable: true });

  protected get toolbarActions(): PageToolbarAction[] {
    return this.canManageRoles()
      ? [
          {
            id: 'create',
            icon: 'plus',
            titleTranslateKey: 'monitoring.roles.dashboard.add-role',
          },
        ]
      : [];
  }

  protected onToolbarAction(actionId: string): void {
    if (actionId === 'create') {
      this.openRoleModal('monitoring.roles.modal.role-creation', RolesAction.Create);
    }
  }

  public ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string) => {
        this.pagination.set({ pageIndex: 1, pageSize: this.pagination().pageSize });
        this.rolesService.triggerRolesUpdate({ skip: 0, keyword: value });
      });
  }

  public onPageChange(paginationEvent: IPaginationEvent): void {
    this.pagination.set(paginationEvent);
    this.rolesService.triggerRolesUpdate({
      skip: (paginationEvent.pageIndex - 1) * paginationEvent.pageSize,
      take: paginationEvent.pageSize,
    });
  }

  public openRoleModal(title: string, action: RolesAction, roleId?: string): void {
    if (action === RolesAction.Create && !this.canManageRoles()) return;

    this.modalService
      .open<IRoleModalData, boolean, IConfirmationDialogData, IRoleModalFormChecker>({
        component: RoleModalComponent,
        data: {
          title,
          action,
          roleId,
        },
        customContainerClass: 'role-modal',
        confirmationComponent: ConfirmationDialogComponent,
        shouldConfirmClose: (component: IRoleModalFormChecker): boolean => component.isFormModified,
        confirmationData: {
          headerTranslationKey: 'monitoring.roles.modal.unsaved-changes-title',
          descriptionTranslationKey: 'monitoring.roles.modal.unsaved-changes-text',
          approvalTranslationKey: 'monitoring.roles.modal.close',
          cancellationTranslationKey: 'monitoring.roles.modal.stay',
        },
        confirmationContainerClass: 'roles-closure-confirm-modal',
      })
      .afterClose$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        filter((result) => !!result),
      )
      .subscribe(() => this.rolesService.triggerRolesUpdate({}));
  }
}
